import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Edit3,
    Archive,
    ArchiveRestore,
    Send,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";

/**
 * Student Detail page header bar — back link, prev/next nav, Edit, Archive
 * (or Restore when archived), and admin-only Transfer-to-teacher actions.
 *
 * Pure composition: all mutations come from the data context via the props
 * the parent passes in.
 */
export function StudentDetailHeader({
    student,
    students,
    isAdmin,
    teacherOptions,
    archiveStudent,
    restoreStudent,
    transferStudent,
}) {
    const navigate = useNavigate();
    const studentIndex = students.findIndex((s) => s.id === student.id);
    const prevStudent = studentIndex > 0 ? students[studentIndex - 1] : null;
    const nextStudent =
        studentIndex !== -1 && studentIndex < students.length - 1
            ? students[studentIndex + 1]
            : null;
    const totalStudents = students.length;
    const isArchived = !!student.archivedAt;

    const onArchive = () => {
        archiveStudent(student.id);
        toast.success(`${student.name} archived. History kept safe.`);
        navigate("/students");
    };
    const onRestore = () => {
        restoreStudent(student.id);
        toast.success(`${student.name} restored`);
    };

    const [transferOpen, setTransferOpen] = useState(false);
    const [transferTo, setTransferTo] = useState("");

    const onTransfer = () => {
        const saved = transferStudent(student.id, transferTo);
        if (saved) {
            const target = teacherOptions.find((t) => t.id === transferTo);
            toast.success(
                `Transferred ${student.name} to ${target?.name || "new workspace"}`,
            );
            setTransferOpen(false);
            setTransferTo("");
            navigate("/students");
        }
    };

    return (
        <div className="flex items-center justify-between gap-3">
            <Link
                to="/students"
                className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800"
                data-testid="back-link"
            >
                <ArrowLeft size={16} strokeWidth={2.8} />
                Back
            </Link>

            <div className="flex items-center gap-2 flex-wrap justify-end">
                {totalStudents > 1 && !isArchived && (
                    <div
                        className="inline-flex items-center rounded-xl border border-mist bg-white overflow-hidden"
                        data-testid="student-nav"
                    >
                        <button
                            type="button"
                            onClick={() =>
                                prevStudent && navigate(`/students/${prevStudent.id}`)
                            }
                            disabled={!prevStudent}
                            data-testid="student-nav-prev"
                            aria-label="Previous student"
                            className="focus-ring h-10 px-3 inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:bg-mist/50 disabled:opacity-40 disabled:cursor-not-allowed border-r border-mist transition-colors"
                        >
                            <ChevronLeft size={15} strokeWidth={2.8} />
                            <span className="hidden sm:inline">Prev</span>
                        </button>
                        <span
                            className="px-3 text-[11px] font-bold text-slate-500 tabular-nums"
                            data-testid="student-nav-position"
                        >
                            {studentIndex + 1} / {totalStudents}
                        </span>
                        <button
                            type="button"
                            onClick={() =>
                                nextStudent && navigate(`/students/${nextStudent.id}`)
                            }
                            disabled={!nextStudent}
                            data-testid="student-nav-next"
                            aria-label="Next student"
                            className="focus-ring h-10 px-3 inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:bg-mist/50 disabled:opacity-40 disabled:cursor-not-allowed border-l border-mist transition-colors"
                        >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRight size={15} strokeWidth={2.8} />
                        </button>
                    </div>
                )}

                {!isArchived && (
                    <Link to={`/students/${student.id}/edit`}>
                        <Button
                            variant="outline"
                            className="focus-ring rounded-xl h-10 px-4 font-bold border-mist hover:bg-mist/50"
                            data-testid="edit-student-btn"
                        >
                            <Edit3 size={15} strokeWidth={2.5} className="mr-2" />
                            Edit
                        </Button>
                    </Link>
                )}

                {isAdmin && !isArchived && teacherOptions.length > 0 && (
                    <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="focus-ring rounded-xl h-10 px-4 font-bold border-mist hover:bg-mist/50"
                                data-testid="transfer-student-btn"
                            >
                                <Send size={15} strokeWidth={2.5} className="mr-2" />
                                Transfer
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-3xl">
                            <DialogHeader>
                                <DialogTitle className="font-display uppercase tracking-tight">
                                    Transfer {student.name}
                                </DialogTitle>
                                <DialogDescription>
                                    Move this student and all their attendance history to
                                    another teacher&apos;s workspace.
                                </DialogDescription>
                            </DialogHeader>
                            <div
                                className="space-y-2"
                                data-testid="transfer-teacher-options"
                            >
                                {teacherOptions.map((t) => (
                                    <label
                                        key={t.id}
                                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                            transferTo === t.id
                                                ? "border-[#3D6385] bg-action-soft"
                                                : "border-mist hover:border-mist"
                                        }`}
                                        data-testid={`transfer-option-${t.id}`}
                                    >
                                        <input
                                            type="radio"
                                            name="transfer-to"
                                            value={t.id}
                                            checked={transferTo === t.id}
                                            onChange={() => setTransferTo(t.id)}
                                            className="accent-[#3D6385]"
                                        />
                                        <div className="flex-1">
                                            <div className="font-bold text-sm text-slate-900">
                                                {t.name}
                                            </div>
                                            {t.email && (
                                                <div className="text-xs text-slate-500">
                                                    {t.email}
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setTransferOpen(false)}
                                    className="rounded-xl font-bold"
                                    data-testid="transfer-cancel-btn"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={onTransfer}
                                    disabled={!transferTo}
                                    className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="transfer-confirm-btn"
                                >
                                    Transfer
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {isArchived ? (
                    <Button
                        onClick={onRestore}
                        className="focus-ring rounded-xl h-10 px-4 bg-success hover:bg-success text-white font-bold"
                        data-testid="restore-student-btn"
                    >
                        <ArchiveRestore
                            size={15}
                            strokeWidth={2.5}
                            className="mr-2"
                        />
                        Restore
                    </Button>
                ) : (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="outline"
                                className="focus-ring rounded-xl h-10 px-4 font-bold text-warning border-warning/30 hover:bg-warning-soft hover:text-warning"
                                data-testid="archive-student-btn"
                            >
                                <Archive size={15} strokeWidth={2.5} className="mr-2" />
                                Archive
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-3xl">
                            <AlertDialogHeader>
                                <AlertDialogTitle className="font-display uppercase tracking-tight">
                                    Archive {student.name}?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    They&apos;ll be hidden from active students, schedules,
                                    and dashboards. Their attendance history is kept and
                                    you can restore them any time from the Archived tab.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel
                                    className="rounded-xl font-bold"
                                    data-testid="archive-cancel-btn"
                                >
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={onArchive}
                                    className="rounded-xl bg-warning hover:bg-warning/85 font-bold"
                                    data-testid="archive-confirm-btn"
                                >
                                    Archive
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </div>
        </div>
    );
}
